import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AuthenticationService as SDKAuthenticationService,
  filterNullOrUndefined,
  Kaza,
  KazaService as SDKKazaService,
  Tag,
  TagService as SDKTagService,
  User,
  ProjectTypeService as SDKProjectTypes,
  ProjectType
} from '@adeo/ngx-kozikaza-api';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AuthPopinService } from '../auth-popin.service';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { FormCheckboxService } from '../../../shared/services/utilities/form-checkbox.service';
import { ToastrService } from '../../../shared/services/utilities/toastr.service';
import { HttpParams } from '@angular/common/http';
import { KazaService as LegacyKazaService } from '../../../shared/services/features/kaza/kaza.service';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { RoutesService } from '../../../shared/services/routes/routes.service';

@Component({
  selector: 'app-tags',
  templateUrl: './tags.component.html',
  styleUrls: ['./tags.component.scss']
})
export class TagsComponent implements OnInit, OnDestroy {
  tags: Tag[];
  kaza: any;
  step: number;
  formArray: UntypedFormArray;
  tagsForm: UntypedFormGroup;
  submitted = false;
  hasError = false;
  currentUser: User;
  subscriptionProjectAddTags: Subscription;
  subscriptionActivateRoute: Subscription;
  gtmForm: string;
  gtmCancel: string;
  gtmSubmit: string;
  loadingTags = true;
  projectType :Array<string>;
  isInKazaplan$: Observable<boolean> = this.routesService.routeDatas.pipe(
    map((data) => !!data.isKazaplanRoute),
    share(),
  );
  subProjectTypes: Subscription;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private formCheckboxService: FormCheckboxService,
    private fb: UntypedFormBuilder,
    private authPopinService: AuthPopinService,
    private sdkKazaService: SDKKazaService,
    private legacyKazaService: LegacyKazaService,
    private sdkTagService: SDKTagService,
    private toastr: ToastrService,
    private sdkAuthenticationService: SDKAuthenticationService,
    private authService: AuthService,
    private readonly userStoreService: UserStoreService,
    private readonly routesService: RoutesService,
    private readonly cdr: ChangeDetectorRef,
    private readonly sdkProjectTypesService: SDKProjectTypes,

  ) {
  }

  ngOnInit() {
    this.subscriptionActivateRoute = this.activatedRoute.params.pipe(
      distinctUntilChanged((prev, curr) => prev.step === curr.step),
      tap(() => this.buildForm()),
      switchMap((data) => {
        this.step = parseInt(data.step, 10);
        this.gtmForm = (this.step === 1) ? 'dataProjetsTravaux_save' : 'dataProjetsAmenagement_form';
        this.gtmCancel = (this.step === 1) ? 'dataProjetsTravaux_cancel' : 'dataProjetsAmenagement_cancel';
        this.gtmSubmit = (this.step === 1) ? 'dataProjetsTravaux_save' : 'dataProjetsAmenagement_save';
        return this.loadKaza();
      }),
      switchMap((kaza) => {
        this.kaza = kaza;
        return this.loadProjectType();
      }),
      switchMap((projectType) => {
        this.projectType = projectType;
        if (this.projectType?.length === 1 && this.projectType?.includes('energetic-renovation') && this.step === 1) {
          return this.loadTags({});
        } else {
          return this.loadTags({ registrationStep: this.step.toString() });
        }
      }),
      tap((tags) => {
        if(this.projectType.length === 1 && this.projectType.includes('energetic-renovation') && this.step === 1){
          this.tags = tags.filter((item) => ['/tags/a793bc48-c7b2-4746-9d10-89abcabd3b81', '/tags/ffe4ed1c-c885-4abd-979c-074206471808', '/tags/736b0fd4-0099-493c-8c45-1005624a19f1', '/tags/626ec1b9-d0d2-4d2d-8559-16fcf80dac42'].includes(item?.['@id']));
        }else {
          this.tags = tags
        }
        this.formCheckboxService.buildCheckbox(this.formArray, this.tags, (this.kaza && this.kaza.tags) ? this.kaza.tags : []);
        this.tagsForm.clearValidators();
        this.cdr.detectChanges();
      }),
      catchError((err) => {
        this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        return throwError(err);
      })
    ).subscribe();
  }
  ngOnDestroy(): void {
    if (this.subscriptionProjectAddTags && !this.subscriptionProjectAddTags.closed) {
      this.subscriptionProjectAddTags.unsubscribe();
    }
    if (this.subscriptionActivateRoute && !this.subscriptionActivateRoute.closed) {
      this.subscriptionActivateRoute.unsubscribe();
    }
  }
  private loadProjectType(): Observable<any> {
    return this.sdkProjectTypesService.getProjectTypes(null).pipe(
      catchError((e) => {
        this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        return throwError(e);
      }),
      map((data) => {
        const kazaType = this.kaza?.types.map((type) => {
          const typeMapped = data['hydra:member'].find((projectType) => projectType['@id'] === ((typeof type === 'string') ? type : type['@id']));
          return typeMapped?.type;
        })
        return kazaType;
      }),
    );
  }
  
  private loadTags(queryParam: any): Observable<Array<Tag>> {
    this.tags = [];
    this.loadingTags = true;
    return this.sdkTagService.getTags(null, { query: new HttpParams({ fromObject: queryParam }) }).pipe(
      finalize(() => {
        this.loadingTags = false;
        this.cdr.detectChanges();
      }),
      catchError((e) => {
        this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
        return throwError(e);
      }),
      map((data) => data['hydra:member'])
    );
  }

  private loadKaza(): Observable<Kaza> {
    return combineLatest([
      this.userStoreService.userStore.pipe(
        filterNullOrUndefined(),
      ),
      this.userStoreService.userStoreActiveKaza.pipe(
        filterNullOrUndefined(),
      ),
    ]).pipe(
      switchMap(([user, kaza]) => {
        this.currentUser = user;
        return this.sdkKazaService.getKaza(kaza, ['tags']).pipe(
          catchError((e) => {
            this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
            return throwError(e);
          })
        );
      })
    );
  }
 
  isFieldInvalid(name: string): boolean {
    if (!this.tagsForm) {
      return false;
    }

    const control = this.tagsForm.get(name);
    if (!control) {
      return false;
    }
    return (this.hasError || control.dirty || control.touched) && control.invalid;
  }

  buildForm(): void {
    this.formArray = new UntypedFormArray([]/*, minCheckboxSelected(1)*/);
    this.tagsForm = this.fb.group({
      tags: this.formArray
    });
  }

  save(): void {
    if (this.tagsForm.valid) {
      const tags = [];
      this.hasError = false;
      this.submitted = true;

      for (let i = 0; i < this.tagsForm.value.tags.length; i++) {
        if (true === this.tagsForm.value.tags[i]) {
          tags.push(this.tags[i]['@id']);
        }
      }

      const otherStep = (this.step === 1) ? 2 : 1;
      for (const kazaTags of this.kaza.tags) {
        if (kazaTags.registrationStep === otherStep) {
          tags.push(kazaTags['@id']);
        }
      }

      this.subscriptionProjectAddTags = this.legacyKazaService.update(
        (typeof this.userStoreService.userActiveKaza === 'string')
          ? this.userStoreService.userActiveKaza : this.userStoreService.userActiveKaza['@id'],
        {tags}
      ).pipe(
        catchError((e) => {
          this.toastr.error($localize`:@@toast.error_try_again:An error has occurred. Please try again`);
          return throwError(e);
        }),
        finalize(() => this.submitted = false),
        tap(() => this.next())
      ).subscribe();
    } else {
      this.hasError = true;
    }
  }

  cancel(): void {
    this.setHasProposed(true);
    this.authPopinService.closePopinAndRedirect();
  }

  next(): void {
    this.setHasProposed();
    if (this.step === 2) {
      this.authPopinService.closePopinAndRedirect();
    } else {
      const step2IsUncompleted = this.authService.kazaTagStep2IsUncompleted(this.userStoreService.user);
      if(step2IsUncompleted) {
        this.authService.redirectToTags(2);
      } else {
        this.authPopinService.closePopinAndRedirect();
      }
    }
  }

  previous():void  {
    if(this.step === 1){
     this.userStoreService.previousAuthTagsEnabled = true;
     this.redirectToConstructionDateStep()
    } 
    else if(this.step === 2){
     this.userStoreService.previousAuthTagsStepTwoEnabled = true;
     this.redirectToTags(1);
    }
   }
   redirectToTags(step: number): void {
     this.router.navigate([{
       outlets: {
         auth: ['authentification', 'tags', step.toString()]
       }
     }]).catch(() => {});
   }
   redirectToConstructionDateStep(): void {
     this.router.navigate([{
       outlets: {
         auth: ['authentification', 'construction-date']
       }
     }]).catch((e) => {console.log(e)});
   }
  setHasProposed(both: boolean = false): void {
    if (this.step === 1 || both) {
      this.authService.step1HasBeenProposed = true;
    }
    if (this.step === 2 || both) {
      this.authService.step2HasBeenProposed = true;
    }
  }
}
